import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { Image, Platform } from 'react-native';

/**
 * Perfis de compressão por uso. O lado maior da imagem é limitado a `maxSide`
 * e ela é regravada em JPEG — o que também converte HEIC do iPhone, que o
 * navegador não exibe.
 */
const PRESETS = {
  avatar: { maxSide: 512, quality: 0.8 },
  logo: { maxSide: 800, quality: 0.85 },
  photo: { maxSide: 1600, quality: 0.75 },
  // Comprovante precisa continuar legível (valores, códigos).
  proof: { maxSide: 2000, quality: 0.8 },
} as const;

export type ImageUploadPreset = keyof typeof PRESETS;

interface PreparedImage {
  uri: string;
  name: string;
  type: string;
}

function getImageSize(uri: string): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    Image.getSize(
      uri,
      (width, height) => resolve({ width, height }),
      () => resolve(null),
    );
  });
}

function isPng(uri: string): boolean {
  return uri.startsWith('data:image/png') || /\.png$/i.test(uri);
}

/**
 * Reduz e recomprime a imagem antes do upload. Logo em PNG continua PNG
 * (transparência). Se a manipulação falhar, devolve o original — o backend
 * ainda valida tipo e tamanho.
 */
export async function prepareImageForUpload(
  uri: string,
  preset: ImageUploadPreset,
  baseName = 'image',
): Promise<PreparedImage> {
  const { maxSide, quality } = PRESETS[preset];
  const keepPng = preset === 'logo' && isPng(uri);

  try {
    const size = await getImageSize(uri);
    const actions: Parameters<typeof manipulateAsync>[1] = [];
    if (size && Math.max(size.width, size.height) > maxSide) {
      actions.push({
        resize: size.width >= size.height ? { width: maxSide } : { height: maxSide },
      });
    }

    const result = await manipulateAsync(uri, actions, {
      compress: quality,
      format: keepPng ? SaveFormat.PNG : SaveFormat.JPEG,
    });

    return keepPng
      ? { uri: result.uri, name: `${baseName}.png`, type: 'image/png' }
      : { uri: result.uri, name: `${baseName}.jpg`, type: 'image/jpeg' };
  } catch {
    return {
      uri,
      name: `${baseName}.${keepPng ? 'png' : 'jpg'}`,
      type: keepPng ? 'image/png' : 'image/jpeg',
    };
  }
}

/** Adiciona a imagem já tratada ao multipart (web usa Blob; nativo, o objeto do RN). */
export async function appendImageToFormData(
  formData: FormData,
  field: string,
  image: PreparedImage,
): Promise<void> {
  if (Platform.OS === 'web') {
    const response = await fetch(image.uri);
    const blob = await response.blob();
    formData.append(field, blob, image.name);
  } else {
    formData.append(field, {
      uri: Platform.OS === 'ios' ? image.uri.replace('file://', '') : image.uri,
      name: image.name,
      type: image.type,
    } as any);
  }
}

/** Atalho: comprime e anexa. */
export async function appendCompressedImage(
  formData: FormData,
  field: string,
  uri: string,
  preset: ImageUploadPreset,
): Promise<void> {
  const image = await prepareImageForUpload(uri, preset, field);
  await appendImageToFormData(formData, field, image);
}
